import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

// FBR salaried income-tax slabs for tax year 2026-27 (effective 1 July 2026).
// Each bracket is the upper bound of taxable annual income and the marginal
// rate applied to the portion of income falling within it. The 9% surcharge
// on high earners was abolished for salaried persons this year.
const SLABS = [
  { upTo: 600000, rate: 0 },
  { upTo: 1200000, rate: 0.01 },
  { upTo: 2200000, rate: 0.11 },
  { upTo: 3200000, rate: 0.2 },
  { upTo: 4100000, rate: 0.25 },
  { upTo: 5600000, rate: 0.29 },
  { upTo: 7000000, rate: 0.32 },
  { upTo: Infinity, rate: 0.35 },
];

const MODES = [
  { id: "monthly", label: "Monthly salary" },
  { id: "annual", label: "Annual salary" },
];

// Progressive tax on an annual taxable income using the slab table above.
function computeAnnualTax(annualIncome) {
  let tax = 0;
  let lower = 0;
  for (const slab of SLABS) {
    if (annualIncome <= lower) break;
    const taxableInSlab = Math.min(annualIncome, slab.upTo) - lower;
    tax += taxableInSlab * slab.rate;
    lower = slab.upTo;
  }
  return tax;
}

const fmt = (n) =>
  Number(n).toLocaleString("en-PK", { maximumFractionDigits: 0 });

function IncomeTaxCalculatorPk() {
  const [mode, setMode] = useState("monthly");
  const [income, setIncome] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const reset = (nextMode) => {
    setMode(nextMode);
    setIncome("");
    setResult(null);
    setError("");
    setShowFunFact(false);
  };

  const onInput = (e) => {
    setIncome(e.target.value);
    setResult(null);
    setError("");
    setShowFunFact(false);
  };

  const calculate = () => {
    const value = parseFloat(income);
    if (isNaN(value) || value < 0) {
      setError("Please enter a valid salary amount.");
      setResult(null);
      setShowFunFact(false);
      return;
    }

    const annualIncome = mode === "monthly" ? value * 12 : value;
    const annualTax = computeAnnualTax(annualIncome);
    const annualTakeHome = annualIncome - annualTax;
    const effectiveRate = annualIncome > 0 ? (annualTax / annualIncome) * 100 : 0;

    setError("");
    setResult({
      annualTax,
      monthlyTax: annualTax / 12,
      annualTakeHome,
      monthlyTakeHome: annualTakeHome / 12,
      effectiveRate,
    });

    const funFactsList = [
      "Income up to Rs 600,000 a year is completely tax-free for salaried persons.",
      "Pakistan uses a marginal system — a higher slab only taxes the portion of income above its threshold, not your whole salary.",
      "The 9% surcharge on high earners was abolished for the salaried class this year.",
      "Your effective tax rate is always lower than your top slab rate because the first Rs 600,000 is exempt.",
      "Tax-adjusted investments like pension funds can reduce your taxable income.",
    ];
    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="Pakistan Income Tax Calculator 2026-27"
      metaDescription="Free Pakistan salary income tax calculator for FBR tax year 2026-27. Enter your monthly or annual salary to see your income tax, take-home pay, and effective tax rate."
      intro={
        <>
          Work out your <strong>salaried income tax</strong> under the latest
          FBR slabs for tax year <strong>2026-27</strong> (effective 1 July
          2026), plus your monthly take-home pay and effective tax rate.
        </>
      }
      seoContent={
        <>
          <h2>How salaried income tax is calculated in Pakistan</h2>
          <p>
            Pakistan taxes salaried income progressively: your income is split
            across slabs and each slab's rate applies only to the portion of
            income that falls within it. The first Rs 600,000 of annual income
            is exempt. From Rs 600,001 to Rs 1,200,000 the rate is 1%; it then
            rises through 11%, 20%, 25%, 29% and 32%, reaching 35% on income
            above Rs 7,000,000. Because the lower slabs are always taxed at
            their own lower rates, your effective (average) tax rate is lower
            than the top slab rate you reach.
          </p>
          <h2>Tax year 2026-27 changes</h2>
          <p>
            The Finance Act for 2026-27 reduced rates in the middle brackets
            (for example the Rs 2.2–3.2 million slab dropped from 23% to 20%)
            and abolished the 9% surcharge that previously applied to salaried
            individuals with very high incomes. This calculator uses those
            current FBR slabs. It covers salaried individuals only and is an
            estimate for guidance — your employer's payroll or a tax
            professional can confirm your exact liability, including any
            allowances, tax credits, or other adjustments.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>What is the tax-free income limit in Pakistan for 2026-27?</h3>
          <p>
            Salaried individuals pay no income tax on the first Rs 600,000 of
            annual income. Tax only begins on income above that threshold.
          </p>
          <h3>Is my whole salary taxed at my slab rate?</h3>
          <p>
            No. Pakistan uses a marginal system — each slab's rate applies only
            to the portion of income within that slab, so your effective
            (average) rate is always lower than your top slab rate.
          </p>
          <h3>Does this include the surcharge?</h3>
          <p>
            The 9% surcharge that previously applied to high salaried incomes
            was abolished for the salaried class in the 2026-27 budget, so it is
            not part of this calculation.
          </p>
        </>
      }
    >
      <div className="mode-tabs">
        {MODES.map((m) => (
          <button
            key={m.id}
            className={`mode-tab ${mode === m.id ? "active" : ""}`}
            onClick={() => reset(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="fields">
        <label className="field">
          <span>{mode === "monthly" ? "Monthly Salary (PKR)" : "Annual Salary (PKR)"}</span>
          <input
            type="number"
            value={income}
            onChange={onInput}
            placeholder={mode === "monthly" ? "e.g. 150000" : "e.g. 1800000"}
          />
        </label>
      </div>

      <button className="btn" onClick={calculate}>Calculate Tax</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <>
          <div className="result result-breakdown">
            <div>
              Monthly tax: <strong>Rs {fmt(result.monthlyTax)}</strong>
            </div>
            <div>
              Annual tax: <strong>Rs {fmt(result.annualTax)}</strong>
            </div>
            <div>
              Monthly take-home: <strong>Rs {fmt(result.monthlyTakeHome)}</strong>
            </div>
            <div>
              Annual take-home: <strong>Rs {fmt(result.annualTakeHome)}</strong>
            </div>
            <div>
              Effective tax rate:{" "}
              <strong>{result.effectiveRate.toFixed(2)}%</strong>
            </div>
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default IncomeTaxCalculatorPk;
